import React, { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { logger } from '../../utils/logger';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
}

function getUserFriendlyMessage(error: Error | null): string {
    const msg = error?.message?.toLowerCase() ?? '';

    if (msg.includes('failed to fetch') || msg.includes('network') || msg.includes('api')) {
        return 'The service is currently unreachable. Please check your connection or try again in a moment.';
    }

    if (msg.includes('invalid environment configuration')) {
        return 'A configuration issue was detected. Please contact support or verify environment settings.';
    }

    return 'An unexpected error occurred. Please try again or return to the home page.';
}

class ErrorBoundary extends Component<Props, State> {
    public constructor(props: Props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null
        };
    }

    public static getDerivedStateFromError(error: Error): Partial<State> {
        return {
            hasError: true,
            error
        };
    }

    public override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        const correlationId = crypto.randomUUID();

        logger.error('React component render error caught by ErrorBoundary', {
            correlationId,
            error: error.message,
            stack: error.stack,
            componentStack: errorInfo.componentStack,
            path: window.location.pathname,
            url: window.location.href,
            userAgent: navigator.userAgent
        });

        this.setState({ errorInfo });
    }

    private handleReset = (): void => {
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null
        });
    };

    private handleGoHome = (): void => {
        window.location.href = '/';
    };

    public override render(): ReactNode {
        const { hasError, error, errorInfo } = this.state;
        const { children, fallback } = this.props;

        if (!hasError) {
            return children;
        }

        if (fallback) {
            return fallback;
        }

        const message = getUserFriendlyMessage(error);

        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
                <div className="max-w-md w-full bg-white dark:bg-gray-800 shadow-lg rounded-lg p-6 border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center mb-4">
                        <svg
                            className="w-12 h-12 text-red-600 mr-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                            />
                        </svg>
                        <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">Something went wrong</h1>
                    </div>

                    <p className="text-gray-700 dark:text-gray-300 mb-6">{message}</p>

                    <div className="flex gap-3">
                        <button
                            onClick={this.handleReset}
                            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2 rounded transition-colors"
                        >
                            Try Again
                        </button>
                        <button
                            onClick={this.handleGoHome}
                            className="flex-1 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-gray-100 font-medium px-4 py-2 rounded transition-colors"
                        >
                            Go Home
                        </button>
                    </div>

                    {errorInfo && (
                        <details className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                            <summary className="cursor-pointer text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200">
                                Error Details
                            </summary>
                            <pre className="mt-3 text-xs bg-gray-100 dark:bg-gray-900 p-3 rounded overflow-auto max-h-48 text-gray-800 dark:text-gray-200">
                                {errorInfo.componentStack}
                            </pre>
                        </details>
                    )}
                </div>
            </div>
        );
    }
}

export default ErrorBoundary;
